import { KeyboardEvent, MouseEvent, Ref } from "react";
import { Link } from "react-router";
import { ChevronRight, Globe } from "lucide-react";

import { CollectionNode } from "@/common/collectionTree";
import { collectionPath } from "@/common/urls";
import { cn } from "@/lib/utils";
import CollectionPinButton from "./CollectionPinButton";
import CollectionRowTooltip from "./CollectionRowTooltip";
import { SidebarMenuButton, SidebarMenuItem } from "../sidebar";

// The list shows one level at a time, so a row carries no indent and no guides
// and splits into at most three targets, each of them big enough for a thumb
// below `md` — the width at which the sidebar becomes a sheet.
const ROW_H = "h-11 md:h-[34px]";
// The two trailing controls float over the end of the link rather than sitting
// beside it, so every row is one full-width target with the same reserved right
// edge — whether or not it has a chevron, and whether or not its pin is showing.
// That reservation is what keeps a level's counts in one column.
const DRILL_W = "w-11 md:w-7";
const PIN_X = "right-0 md:right-7";
// Mobile: 44 chevron + 12. Desktop: 28 chevron + 20 pin + 8, or 28 + 12 without.
const TRAIL_PAD = "pr-14";
const TRAIL_PAD_NO_PIN = "md:pr-10";

export default function CollectionRow({
  node,
  isActive,
  count,
  canPin,
  onDrill,
  onNavigate,
  onUp,
  onActiveClick,
  ref,
}: {
  node: CollectionNode;
  isActive: boolean;
  /** Records in the current interval, or undefined when the collection has none. */
  count?: number;
  canPin: boolean;
  /** Descend into this collection's own children. */
  onDrill: () => void;
  /** The link was followed — as opposed to swallowed by `onActiveClick`. */
  onNavigate?: () => void;
  /** Leave this level for the one above, from the keyboard. */
  onUp?: () => void;
  /** Clicking the already-open collection runs this instead of navigating:
   * it shows all of the collection's records, or flips the time/notes tab. */
  onActiveClick?: (() => void) | null;
  ref?: Ref<HTMLLIElement>;
}) {
  const { collection } = node;
  const hasChildren = node.children.length > 0;
  const isPinned = collection.pin_position !== null;
  // The collection is also a record — see §16. Nothing to do with whether it
  // goes deeper: the chevron says that.
  const hasTitleRecord = collection.title_record_id !== null;

  const drill = (event: MouseEvent | KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDrill();
  };

  const handleClick = (event: MouseEvent) => {
    if (isActive && onActiveClick) {
      event.preventDefault();
      onActiveClick();
      return;
    }
    onNavigate?.();
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight" && hasChildren) drill(event);
    if (event.key === "ArrowLeft" && onUp) {
      event.preventDefault();
      onUp();
    }
  };

  return (
    <SidebarMenuItem
      ref={ref}
      className="group/collection flex items-stretch border-b border-sidebar-border/30 last:border-b-0"
    >
      <CollectionRowTooltip collection={collection}>
        <SidebarMenuButton
          asChild
          isActive={isActive}
          className={cn(
            "min-w-0 flex-1 gap-2 rounded-none px-3 text-[13px] text-ink2",
            "data-[active=true]:font-semibold data-[active=true]:[box-shadow:inset_2px_0_0_var(--primary)]",
            ROW_H,
            TRAIL_PAD,
            !canPin && TRAIL_PAD_NO_PIN,
          )}
        >
          <Link
            to={collectionPath(collection)}
            aria-current={isActive ? "page" : undefined}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            {/* Marks the collections that are themselves a record, and is
                reserved on the ones that are not so a level's titles start at
                one x. */}
            <span
              aria-hidden
              className={cn(
                "size-[5px] shrink-0 rounded-full",
                hasTitleRecord ? "bg-primary" : "bg-transparent",
              )}
            />
            <span className="min-w-0 flex-1 truncate">{collection.title}</span>
            <span className="flex shrink-0 items-center gap-1.5 text-muted-foreground [[data-active=true]_&]:text-sidebar-accent-foreground">
              {/* What is nested below this row. Not a record count — those are
                  per-collection and would double-count a record filed in both a
                  parent and a child. */}
              {hasChildren && (
                <span className="mr-0.5 font-mono text-[10px] tabular-nums opacity-55">
                  +{node.descendantCount}
                </span>
              )}
              {collection.is_public && <Globe size={12} strokeWidth={0.9} />}
              {count !== undefined && (
                <span className="font-mono text-[11px] tabular-nums">
                  {count}
                </span>
              )}
            </span>
          </Link>
        </SidebarMenuButton>
      </CollectionRowTooltip>

      {/* Pointer-only: it reveals on hover, which a touch screen has no way to
          do, and an invisible target at the edge of every row is exactly what
          made this list hard to hit with a thumb. Touch pins from the
          collection's settings sheet instead. */}
      {canPin && (
        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 hidden items-center md:flex",
            PIN_X,
          )}
        >
          <CollectionPinButton
            collection={collection}
            className={cn(
              "pointer-events-auto",
              !isPinned &&
                "opacity-0 focus-visible:opacity-100 group-hover/collection:opacity-100",
            )}
          />
        </div>
      )}

      {hasChildren && (
        <button
          type="button"
          onClick={drill}
          aria-label={`Show collections in ${collection.title}`}
          className={cn(
            "absolute inset-y-0 right-0 flex items-center justify-center text-muted-foreground",
            "hover:bg-sidebar-border/70 hover:text-sidebar-accent-foreground",
            DRILL_W,
          )}
        >
          <ChevronRight className="size-4" />
        </button>
      )}
    </SidebarMenuItem>
  );
}
